import { Form, FormikProps, FormikValues } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';

import { Button } from 'shared/components/Button';
import { ConsentModal } from 'checkout/components/Tabs/ConsentModal';
import { CreditCardBillingInfos } from 'checkout/components/Payment/CreditCardBillingInfos';
import { FeatureFlags } from 'featureFlags/constants';
import { IBillingInfo } from 'checkout/types/Billing';
import { ITabName } from 'checkout/types/Tab';
import { PaymentType } from 'shared/enums/PaymentType';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import CountryDropdownField from 'checkout/components/AddressForm/CountryDropdownField';
import { BillingFormCreditCard } from 'checkout/components/Billing/BillingFormCreditCard';
import { GiftCard } from 'checkout/components/Payment/GiftCard';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Payment.CreditCard';

export const CreditCard = () => {
  const router = useRouter();
  const formRef = useRef<FormikProps<FormikValues>>(null);

  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);

  const [billingInfoId, setBillingInfoId] = useState(null);
  const [isAddingCreditCard, setIsAddingCreditCard] = useState(false);
  const [isCreditCardSubmitting, setIsCreditCardSubmitting] = useState(false);
  const [consentModal, setConsentModal] = useState({ display: false, consent: false });

  const { billingInfos, connectBillingInfoToOrder, order, changeTab } = useCheckoutContext();

  const isOffer = order?.wantId && isCheckoutOffersEnabled;

  const CCbillingInfos: IBillingInfo[] = Object.values(billingInfos)?.filter(
    (billingInfo) => billingInfo.paymentType === PaymentType.CreditCard,
  );

  const isOrderBillingInfoIdCreditCard = CCbillingInfos.find(
    (CCbillingInfo) => CCbillingInfo?.id === order?.billingInfoId,
  );

  const handleAddCreditCardClick = () => {
    setIsAddingCreditCard(true);
  };

  const handleBillingInfoChange = (id: number) => {
    setBillingInfoId(id);
  };

  const handleContinueToOrderReview = async () => {
    const url = isGuestCheckoutEnabled
      ? {
          asPath: `/checkout?orderNumber=${router.query.orderNumber as string}`,
          query: { orderNumber: router.query.orderNumber },
        }
      : {
          pathname: '/checkout/[orderNumber]',
          query: { orderNumber: router.query.orderNumber },
        };

    // NOTE: remove any query params from appended by other payment providers
    void router.replace(url, null, {
      shallow: true,
    });

    setIsCreditCardSubmitting(true);

    await connectBillingInfoToOrder(billingInfoId);

    changeTab(ITabName.REVIEW);
    setIsCreditCardSubmitting(false);
  };

  const handleDismissConsentModal = (event) => {
    event.preventDefault();
    setConsentModal({ display: false, consent: false });
  };

  useEffect(() => {
    if (isEmpty(CCbillingInfos)) {
      setIsAddingCreditCard(() => isEmpty(CCbillingInfos));
    } else if (CCbillingInfos.length === 1) {
      setBillingInfoId(() => CCbillingInfos[0]?.id);
    } else if (!isOrderBillingInfoIdCreditCard && billingInfoId === null) {
      setBillingInfoId(() => CCbillingInfos[0]?.id);
    }

    return () => setBillingInfoId((prev) => prev);
  }, [CCbillingInfos]);

  useEffect(() => {
    if (order?.paymentType === PaymentType.CreditCard && order?.billingInfoId) {
      setBillingInfoId(() => order?.billingInfoId);
    }
  }, [order?.billingInfoId, order?.paymentType]);

  return (
    <>
      {isAddingCreditCard ? (
        <>
          <BillingFormCreditCard
            consentModal={consentModal}
            formRef={formRef}
            CCbillingInfos={CCbillingInfos}
            setConsentModal={setConsentModal}
            setIsAddingCreditCard={setIsAddingCreditCard}
          />

          {consentModal.display && (
            <ConsentModal
              onOkClick={() => {
                setConsentModal({ display: false, consent: true });
                void formRef.current.submitForm();
              }}
              onCancelClick={handleDismissConsentModal}
              onClose={() => {
                setConsentModal({ display: false, consent: false });
              }}
            />
          )}
        </>
      ) : (
        <>
          <GiftCard />
          <CreditCardBillingInfos
            currentBillingInfoId={billingInfoId}
            onBillingInfoChange={handleBillingInfoChange}
          />
          <CreditCard.AddTextWrapper onClick={handleAddCreditCardClick}>
            &#43;{' '}
            <span data-qa="add_new_credit_card">
              {t(`${TP}.addNewCreditCardLink`, 'Add Credit Card')}
            </span>
          </CreditCard.AddTextWrapper>
          <CreditCard.Button
            data-qa="checkout_payment_submit"
            title={
              isOffer
                ? t(`${TP}.continueToOfferReviewButton`, 'Continue to Offer Review')
                : t(`${TP}.continueToOrderReviewButton`, 'Continue to Order Review')
            }
            onClick={handleContinueToOrderReview}
            loading={isCreditCardSubmitting}
          />
        </>
      )}
    </>
  );
};

CreditCard.Button = styled(Button)`
  font-size: 11px;
  width: 100%;
  text-transform: uppercase;
`;

CreditCard.Form = styled(Form)`
  input,
  select,
  label {
    font-size: 11.5px;
  }
`;

CreditCard.Checkbox = styled.span`
  display: flex;

  input {
    opacity: 0;
    width: 0;
    height: 0;
    padding: 0;

    &:focus + span {
      ${({ theme }) => css`
        box-shadow: 0 0 0 0.05em ${theme.colors.white}, 0 0 0.15em 0.1em ${theme.colors.black};
      `}
    }
  }

  input + span::before {
    content: '';
    width: 10px;
    height: 12px;
    box-shadow: inset 12px 12px ${({ theme }) => theme.colors.black};
    border-radius: 0;
    transition: 100ms transform ease-in-out;
    transform: scale(0);
  }

  input:checked + span::before {
    transform: scale(1);
    position: relative;
    top: -1px;
  }
`;

CreditCard.CheckboxControl = styled.span`
  border-radius: 0;
  border: 1px solid ${({ theme }) => theme.colors.black};
  height: 12px;
  transform: translateY(3px);
  width: 12px;
  display: grid;
  place-items: center;
`;

CreditCard.CheckboxWrapper = styled.label`
  color: ${({ theme }) => theme.colors.black};
  display: grid;
  grid-gap: 16px;
  grid-template-columns: min-content auto;
  padding: 12px 0;
  margin: 12px 0 9px;

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    font-size: 11.5px;
  }
`;

CreditCard.Label = styled.p`
  margin-top: 2px;
`;

CreditCard.AddTextWrapper = styled.p`
  ${variableFontRegular({ size: '11.5px', height: '11.5px' })}
  cursor: pointer;
  padding-block: 32px 48px;
  margin: 0;
  text-decoration: underline;
`;

CreditCard.Back = styled.a`
  display: block;
  text-align: center;
  margin-top: 24px;
  text-decoration: underline;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    width: 315px;
  }
`;

CreditCard.CountryDropdownField = styled(CountryDropdownField)`
  padding-bottom: 24px;
`;
